import AddIcon from "@mui/icons-material/Add";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import { Box, Container, Grid, useTheme } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { Alert, Button, Typography } from "@platform/design-system";
import { useStorage } from "@platform/hooks";
import { useSignedInUser } from "@platform/user/client";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { JSX, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useApi } from "@platform/api";
import { useDebounce } from "@platform/hooks";

import { submitEvent } from "../../utils/tracking";
import { SearchInput } from "../../components";
import { MARKET, ONE_DIGITAL } from "../../constants/client";
import { useAuth } from "../../hooks";
import ClientsTable from "../../components/ClientsTable";
import { BrokerGroupInfo } from "../../contexts/authContext";

import { ClientPageState } from "./types";
import CreateClientDialog from "./createClientDialog";

const csvNameFile = "clientList.csv";

// The header search's "See all clients" row deep-links here with ?q=, which
// wins over the value restored from the previous visit.
const initialSearchValue = (
  deepLinked: string | null,
  restored: string | undefined,
): string => deepLinked ?? restored ?? "";

interface Props {
  brokerId?: number;
}

function Clients({ brokerId }: Props): JSX.Element {
  const { getStorageItem } = useStorage("session");
  const sessionStorageKey = brokerId
    ? `brokerClientPageState-${brokerId}`
    : "clientPageState";

  const clientPageState: ClientPageState =
    getStorageItem(sessionStorageKey) || {};

  const [searchParams] = useSearchParams();
  const deepLinkedQuery = searchParams.get("q");
  const [searchValue, setSearchValue] = useState(
    initialSearchValue(deepLinkedQuery, clientPageState["searchValue"]),
  );

  // "See all clients" while already on this page changes ?q= without a
  // remount, so the URL is followed after the first render too.
  useEffect(() => {
    if (deepLinkedQuery !== null) setSearchValue(deepLinkedQuery);
  }, [deepLinkedQuery]);
  const [isError, setIsError] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);

  const debouncedSearchValue = useDebounce(searchValue, 400);
  const axios = useApi();
  const { brokerGroupInfo } = useAuth() as { brokerGroupInfo: BrokerGroupInfo };

  const [isCreateClientDialogOpen, setIsCreateClientDialogOpen] =
    useState(false);
  const { isAdminOrConsultant: adminOrConsultant } = useSignedInUser();

  const { mutate: exportCSV, isPending: isLoadingExport } = useMutation({
    mutationFn: () =>
      axios.post(
        "/brokerportal/v1/clients/export",
        {},
        {
          responseType: "blob",
          headers: {
            Accept: "text/csv",
          },
        },
      ),
    onSuccess: (response: AxiosResponse<Blob>) => {
      submitEvent("Client List Exported");
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = csvNameFile;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      logger.error("Error while fetching data for CSV", error);
    },
  });

  const handleCSVData = (): void => {
    if (isLoadingExport) return;
    exportCSV();
  };

  const theme = useTheme();

  const showAddClient =
    !adminOrConsultant &&
    brokerGroupInfo?.type !== ONE_DIGITAL &&
    brokerGroupInfo?.variant !== MARKET;

  const handleSetSearchValue = (newValue: string): void => {
    setSearchValue(newValue);
  };

  return (
    <>
      <Container>
        <Box mb={5} mt={brokerId ? 0 : 18}>
          {isError && (
            <Box mt={2} mb={2}>
              <Alert severity="error">
                Something went wrong. Please try again later.
              </Alert>
            </Box>
          )}
          <Grid container mb={4}>
            {!brokerId && (
              <Grid container size={12} justifyContent="space-between">
                <Typography variant="display-medium">Clients</Typography>
                {showAddClient ? (
                  <Button
                    data-testid="add-client-button"
                    variant="primary"
                    startIcon={<AddIcon />}
                    sx={{ marginBottom: 2.5 }}
                    onClick={() => setIsCreateClientDialogOpen(true)}
                  >
                    Add Client
                  </Button>
                ) : null}
              </Grid>
            )}
            <Grid container size={12}>
              {!brokerId && (
                <Grid
                  size={{ md: adminOrConsultant ? 12 : 4 }}
                  display="flex"
                  alignItems="center"
                  my={adminOrConsultant ? 3 : 0}
                >
                  <Typography variant="caption">
                    Total clients <b>{total || 0}</b>
                  </Typography>
                </Grid>
              )}
              <Grid
                container
                size={{ md: adminOrConsultant || brokerId ? 12 : 8 }}
                gap={2}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box sx={{ flexGrow: 1, maxWidth: brokerId ? "100%" : "90%" }}>
                  <SearchInput
                    onChange={(e) => handleSetSearchValue(e.target.value)}
                    fullWidth
                    value={searchValue}
                  />
                </Box>
                {!brokerId && (
                  <Button
                    sx={{ px: 2, height: "2.5rem", width: theme.spacing(19) }}
                    variant="secondary"
                    loading={isLoadingExport}
                    startIcon={<GetAppOutlinedIcon />}
                    onClick={handleCSVData}
                  >
                    Export All
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>

          <ClientsTable
            brokerId={brokerId}
            search={debouncedSearchValue}
            setIsError={setIsError}
            setTotal={setTotal}
          />
        </Box>
      </Container>
      <CreateClientDialog
        open={isCreateClientDialogOpen}
        onClose={() => setIsCreateClientDialogOpen(false)}
        brokerId={brokerId}
      />
    </>
  );
}

export default Clients;
